import { PostApiSpoChatbotBodyMessagesItem } from '@spo/types';
import { VpStarFilledIcon, VpStarIcon } from '@vtmn-play/icons/react';
import { VpButton, VpStarRating, VpTextarea } from '@vtmn-play/react';
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useChatbotRating } from '../../hooks/mutations/useChatbot';
import './rating.scss';

type RatingProps = {
  messages: PostApiSpoChatbotBodyMessagesItem[];
  cancel: () => void;
  endConversation: () => void;
};

export const Rating = ({ messages, cancel, endConversation }: RatingProps) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);

  const mutationSuccess = () => {
    addToast(
      t('chatbot.success-message'),
      'success',
      4500,
      'feedback_success_toast',
    );
    endConversation();
  };

  const mutationError = () => {
    addToast(
      t('chatbot.error-message'),
      'danger',
      4500,
      'feedback_error_toast',
    );
  };

  const { mutate } = useChatbotRating(
    messages,
    rating.toString(),
    comment,
    mutationSuccess,
    mutationError,
  );

  const handleValidate = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter' && !event.shiftKey) {
      setTimeout(() => {
        mutate();
      }, 1500);
    }
  };

  return (
    <div className="chatbot-rating" data-testid="chatbot_rating">
      <div>{t('chatbot.rating.title')}</div>
      <div>{t('chatbot.rating.subtitle')}</div>
      <VpStarRating score={rating}>
        {Array.from(Array(5)).map((_, index) => {
          return index < rating ? (
            <VpStarFilledIcon
              onClick={() => setRating(index + 1)}
              key={index}
            />
          ) : (
            <VpStarIcon
              onClick={() => setRating(index + 1)}
              key={index}
              data-testid={`rating_star_${index}`}
            />
          );
        })}
      </VpStarRating>
      <VpTextarea
        value={comment}
        placeholder={t('chatbot.rating.placeholder')}
        data-testid="chatbot_feedback_textarea"
        onChange={({ currentTarget }) => setComment(currentTarget.value)}
        onKeyUp={handleValidate}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) =>
          event.stopPropagation()
        }
      />
      <div className="rating-buttons">
        <VpButton
          variant="secondary"
          shape="squared"
          onClick={cancel}
          data-testid="rating_button_cancel"
        >
          {t('chatbot.rating.ask-another-question')}
        </VpButton>
        <VpButton
          shape="squared"
          onClick={() => mutate()}
          data-testid="rating_button_validate"
          disabled={rating === 0}
        >
          {t('chatbot.rating.validate-feedback')}
        </VpButton>
      </div>
    </div>
  );
};
